import { useMemo } from 'react';
import { I18n } from '@aws-amplify/core';
import { RISK_FACTOR_CATEGORIES } from '../constants';
import { getReviewId } from '../../../_hooks/getReviewId';
import { useResolution886Report } from '@/context/Resolution886Report';
import { useGetResolution886ReviewResults } from '@/hooks/v2/resolution-886';
import { useGetResolution886CategoryResult } from '@/hooks/v2/resolution-886/useGetResolution886CategoryResult';
import {
	Report,
	Assessment,
	CategoryResult,
	Category,
	Step,
	Task,
	Question
} from '@/context/Resolution886Report/types';

export const useGetRiskFactorCategories = (report: Report, category_key?: string) => {
	const { form, assessment, selectedReviewId, selectedAssessmentId } = useResolution886Report();

	const organization_id = form.getFieldValue(['general_information', 'organization_id']);
	const company_id = form.getFieldValue(['general_information', 'company_id']);
	const assessment_id = assessment.id;

	const reviewId = getReviewId(selectedReviewId, report?.reviews);

	const {
		isLoading,
		data: reviewResults,
		error
	} = useGetResolution886ReviewResults({
		report_id: report?.id,
		company_id: report?.company?.id || '',
		organization_id: report?.organization?.id || '',
		review_id: reviewId
	});

	const {
		isLoading: isLoadingCategoryResults,
		data: categoryResults,
		refetch: refetchCategoryResults,
		isFetching: isFetchingCategoryResults
	} = useGetResolution886CategoryResult({
		assessment_id: selectedAssessmentId || assessment_id,
		company_id,
		organization_id,
		review_id: reviewId,
		category_key: category_key || ''
	});

	const tasks = useMemo(() => {
		if (isLoading || !reviewResults?.assessments) {
			return [];
		}

		return reviewResults.assessments.map((assessment: Assessment, index: number) => {
			const taskId = `task_${index + 1}`;
			const taskName = assessment.activity?.name;
			const assessmentId = assessment.id;

			const categories = assessment.category_results.map((categoryResult: CategoryResult) => {
				const categoryId = `${taskId}_${categoryResult.category.key || categoryResult.category.id}`;
				const categoryName = I18n.get(categoryResult.category?.name || '-');
				const riskLevel = categoryResult.risk || 'TOLERABLE';
				const toolRisk = categoryResult.related_report?.result?.risk || 'NOT_AVALIATED';
				const exposureTime = categoryResult.exposure_time ? Number(categoryResult.exposure_time) : 0;
				const category_result_id = categoryResult.id;

				const getQuestionsForCategory = (
					categoryKey: string,
					riskLevel: string,
					exposureTime: number,
					category_result_id: string | null
				) => {
					const isCurrentCategory = categoryKey === category_key;

					const hasRealData =
						isCurrentCategory &&
						category_result_id &&
						categoryResults?.step_results &&
						categoryResults.step_results.length > 0 &&
						!isLoadingCategoryResults &&
						!isFetchingCategoryResults;

					const categories: Category[] = RISK_FACTOR_CATEGORIES as unknown as Category[];

					if (hasRealData) {
						const identifyTaskStep = categoryResults.step_results.find(
							(step: Step) => step.step.name.includes('identify_task') || step.step.sequence === 2
						);
						const riskLevelStep = categoryResults.step_results.find(
							(step: Step) => step.step.name.includes('risk_level') || step.step.sequence === 3
						);

						const mockCategory = categories.find((cat: Category) => cat.key === categoryKey);

						if (mockCategory) {
							const first_step = mockCategory.mandatory_questions.map(
								(question: Question, index: number) => {
									const realResponse = identifyTaskStep?.questions_results?.[index]?.value === 'YES';
									return {
										sequence: index + 1,
										question: question.text,
										response: realResponse ? 'yes' : 'no'
									};
								}
							);

							const second_step = mockCategory.conditional_questions.map(
								(question: Question, index: number) => {
									const realResponse = riskLevelStep?.questions_results?.[index]?.value === 'YES';
									return {
										sequence: index + 1,
										question: question.text,
										response: realResponse ? 'yes' : 'no'
									};
								}
							);

							return {
								first_step,
								second_step,
								exposure_time: categoryResults.exposure_time,
								category_result_id
							};
						}
					}

					const realCategory = categories.find((cat: Category) => cat.key === categoryKey);

					if (!realCategory) {
						return {
							first_step: [
								{
									sequence: 1,
									question: 'Is the condition present?',
									response: riskLevel !== 'TOLERABLE' ? 'yes' : 'no'
								},
								{
									sequence: 2,
									question: 'Is the exposure significant?',
									response: exposureTime > 5 ? 'yes' : 'no'
								}
							],
							second_step: [
								{
									sequence: 1,
									question: 'Are controls adequate?',
									response: riskLevel === 'TOLERABLE' ? 'yes' : 'no'
								},
								{
									sequence: 2,
									question: 'Is training provided?',
									response: riskLevel !== 'NOT TOLERABLE' ? 'yes' : 'no'
								}
							],
							exposure_time: exposureTime
						};
					}

					const first_step = realCategory.mandatory_questions.map((question: Question, index: number) => ({
						sequence: index + 1,
						question: question.text,
						response: 'no'
					}));

					const second_step = realCategory.conditional_questions.map((question: Question, index: number) => ({
						sequence: index + 1,
						question: question.text,
						response: 'no'
					}));

					return {
						first_step,
						second_step,
						exposure_time: exposureTime,
						category_result_id
					};
				};

				const { first_step, second_step, exposure_time } = getQuestionsForCategory(
					categoryResult.category?.key || '',
					riskLevel,
					exposureTime,
					category_result_id
				);

				const risk_evaluation = categoryResult
					? {
							risk_level: riskLevel,
							technical_support: {
								category: categoryName,
								category_key: categoryResult.category?.key,
								risk: categoryResult.related_report || null
							},
							observations: categoryResult.observation
					  }
					: undefined;

				const action_plan =
					categoryResult.category?.key === category_key && category_result_id && categoryResults?.action_plan
						? {
								task_id: categoryResults.action_plan.id || '-',
								task_name: categoryResults.action_plan.title || '-',
								responsible: categoryResults.action_plan.responsible_user?.name || '-',
								deadline: categoryResults.action_plan.due_date,
								description: categoryResults.action_plan.description || '-',
								subtasks:
									categoryResults.action_plan.tasks?.map((task: Task) => {
										return {
											task_type: task.title || '-',
											responsible:
												task.responsible ||
												task.responsible_user?.name ||
												task.responsible_name ||
												'-',
											date: task.due_date
										};
									}) || []
						  }
						: undefined;

				return {
					id: categoryId,
					name: categoryName,
					key: categoryResult.category.key,
					riskLevel: riskLevel,
					toolRisk: toolRisk,
					exposure_time: exposure_time || exposureTime,
					first_step,
					second_step,
					risk_evaluation,
					action_plan,
					category_result_id
				};
			});

			return {
				id: taskId,
				name: taskName,
				assessmentId,
				categories
			};
		});
	}, [
		reviewResults,
		categoryResults,
		category_key,
		isLoading,
		isLoadingCategoryResults,
		isFetchingCategoryResults,
		refetchCategoryResults,
		selectedReviewId
	]);

	return {
		tasks,
		isLoading: isLoadingCategoryResults || isLoading || isFetchingCategoryResults,
		isFetching: isFetchingCategoryResults,
		error,
		hasCategoryResults: !!categoryResults?.step_results,
		refetchCategoryResults
	};
};
